import { User } from 'common/interfaces/api';
import MainLayout from 'components/v2/layouts/main-layout';
import ForumSection from 'components/v2/modules/forum';
import MakePostBox from 'components/v2/molecules/make-post-box';
import {
  TabButton,
  TabContent,
  Tabs,
  TabsContainer,
} from 'components/v2/molecules/tabs';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { PostItemType } from 'service/timeline';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

import LeftBarSection from '../left-bar/left-bar-section';
import ProfileInfo from './profile-info';
import ProfilePageContext from './profile-page-context';
import ProfilePostsSection from './profile-posts-section';
import ProfilePageRightSidebar from './right-sidebar';

// Profile page tabs enum
enum ProfilePageTabsEnum {
  Home = 'home',
  Forum = 'forum',
}

interface ProfilePagePropsType {
  username: string;
  userProfile: User | null;
  enableTabs?: boolean;
}

const ProfilePageContent = ({
  username,
  userProfile,
  enableTabs = false,
}: ProfilePagePropsType) => {
  const router = useRouter();
  const tabQuery = router.query.tab as string;

  const primeQuery = router.query.prime as string;

  const currentUser = useStore((state) => state.currentUser);
  const isCurrentUser = username === currentUser?.username;
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const showPaymentDialog = useGlobalStore((state) => state.showPaymentDialog);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const [pinPosts, setPinPosts] = useState<PostItemType[]>([]);

  // Determine default tab based on query param
  const defaultTab = useMemo(() => {
    if (enableTabs && tabQuery === 'forum') {
      return ProfilePageTabsEnum.Forum;
    }
    return ProfilePageTabsEnum.Home;
  }, [enableTabs, tabQuery]);

  useEffect(() => {
    if (isCurrentUser) return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id]);

  useEffect(() => {
    if (!isAuthInit || primeQuery !== '1' || !userProfile) return;

    // Logged in but user profile not yet fetched — wait
    if (currentAuth && !currentUser) return;

    if (currentUser) {
      showPaymentDialog({ creator: userProfile });
    } else {
      showLogin({});
    }

    const newQuery = { ...router.query };
    delete newQuery.prime;
    void router.replace(
      { pathname: router.pathname, query: newQuery },
      undefined,
      { shallow: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthInit, primeQuery, currentAuth, currentUser]);

  // Render home content (shared between tabs and non-tabs mode)
  const renderHomeContent = () => (
    <div>
      <div className="">
        {isCurrentUser && <MakePostBox className="mb-4" from="ProfilePage" />}
      </div>
      {userProfile?.id && <ProfilePostsSection userId={userProfile?.id} />}
    </div>
  );

  return (
    <ProfilePageContext.Provider value={{ pinPosts, setPinPosts }}>
      <MainLayout>
        <div className="relative grid grid-cols-mainMd grid-rows-mainMd grid-areas-mainMd lg:grid-cols-mainLg lg:grid-rows-mainLg lg:grid-areas-mainLg xl:grid-cols-mainXl xl:grid-rows-mainXl xl:grid-areas-mainXl">
          <LeftBarSection />
          <main className="mx-auto w-full min-w-0 max-w-full px-3 pb-20 grid-in-mainContent md:w-[600px] md:max-w-[600px] md:px-5">
            <ProfileInfo user={userProfile} className="mb-4" />

            {enableTabs ? (
              <Tabs
                defaultTab={defaultTab}
                onTabChange={(tab) => {
                  // Update URL query without full page reload
                  const newQuery = { ...router.query };
                  if (tab === ProfilePageTabsEnum.Forum) {
                    newQuery.tab = 'forum';
                  } else {
                    delete newQuery.tab;
                  }
                  void router.replace(
                    { pathname: router.pathname, query: newQuery },
                    undefined,
                    { shallow: true },
                  );
                }}
              >
                <div className="sticky top-16 z-50 -mx-3 mb-4 shadow-sm md:mx-0">
                  <TabsContainer>
                    <TabButton tabKey={ProfilePageTabsEnum.Home}>
                      ホーム
                    </TabButton>
                    <TabButton tabKey={ProfilePageTabsEnum.Forum}>
                      掲示板
                    </TabButton>
                  </TabsContainer>
                </div>

                <div className="overflow-x-hidden">
                  <TabContent tabKey={ProfilePageTabsEnum.Home}>
                    {renderHomeContent()}
                  </TabContent>

                  <TabContent tabKey={ProfilePageTabsEnum.Forum}>
                    {userProfile?.id && <ForumSection creator={userProfile} />}
                  </TabContent>
                </div>
              </Tabs>
            ) : (
              renderHomeContent()
            )}
          </main>
          <ProfilePageRightSidebar />
        </div>
      </MainLayout>
    </ProfilePageContext.Provider>
  );
};

export default ProfilePageContent;
